import { Children, createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { bool, func, node, number, shape, string } from 'prop-types';
import { find, isNaN, max, noop } from 'lodash';
import clsx from 'clsx';
import { Trans } from '@lingui/macro';
import { Fade } from '@material-ui/core';
import { AddCircle } from '@material-ui/icons';
import { useTheme } from '@material-ui/core/styles';
import ReactGridLayout from 'react-grid-layout';

import { usePageId } from '../page/PageUtils';
import { usePageStateValue, addPageIdPrefix, stripPageIdPrefix } from '../../../stores/PageStateStore';
import { calcPanelHeight, outerSpacing } from '../panel/PanelStyles';
import useIsPrinting from '../../../utilities/PrintUtilities';
import useLayoutReducer, {
    rowsToPixels,
    pixelsToRows,
    DEFAULT_MIN_PANEL_HEIGHT,
    DEFAULT_MIN_PANEL_WIDTH,
    DEFAULT_DROPPING_ITEM,
    FULL_WIDTH,
    ROW_HEIGHT,
} from './UseLayoutReducer';
import useStyles from './LayoutStyles';
import { topNavHeight } from '../../top-nav/TopNavStyles';
import { pageHeaderHeight } from '../page-header/PageHeaderStyles';
import { filterContainerHeight } from '../filter-container/FilterContainerStyles';
import { getLogger } from '../../../utilities/UseLog';

const log = getLogger('Layout');

const MAXIMIZED_PANEL_KEY = 'expandedPanel';
const HEADER_OFFSET = topNavHeight + pageHeaderHeight + filterContainerHeight;

const LayoutContext = createContext();

export function useLayout() {
    return useContext(LayoutContext);
}

export function useAutoHeight(panelId) {
    const context = useLayout();
    return [context.isAutoHeightPanel(panelId), context.onPanelAutoHeight];
}

export function useMaximized(panelId) {
    const context = useLayout();
    return [context.isMaximizedPanel(panelId), context.onPanelMaximized];
}

function getHeightOptions(panel, theme, isPrinting) {
    const { height, contentHeight, minContentHeight, autoHeight, flexHeight, hideTitle, stateSummaryOptions } =
        panel.props;
    const showTitle = !hideTitle;
    // We always show the panel state summary while printing:
    const showStateSummary = stateSummaryOptions?.show || isPrinting;
    const panelHeight =
        height !== undefined
            ? height
            : calcPanelHeight(
                  theme,
                  showTitle,
                  showStateSummary,
                  // Auto-height panels may not have a contentHeight, so fallback to minContentHeight if present.
                  autoHeight ? contentHeight || minContentHeight : contentHeight
              );
    const minPanelHeight = calcPanelHeight(theme, showTitle, showStateSummary, minContentHeight);

    return {
        h: isNaN(panelHeight) ? undefined : panelHeight,
        minH: isNaN(minPanelHeight) ? undefined : minPanelHeight,
        autoHeight: (!height || isPrinting) && autoHeight,
        flexHeight,
    };
}

export function getLayoutOptions(panel, theme, isPrinting) {
    const { x, y, width: w, minWidth: minW, disableVerticalResize = false } = panel.props;
    const { h, minH, autoHeight, flexHeight } = getHeightOptions(panel, theme, isPrinting);
    return {
        x,
        y,
        w,
        minW,
        h,
        minH,
        autoHeight,
        flexHeight,
        disableVerticalResize,
    };
}

/*
 *  Get panel coordinates (x,y,w) from either the layout item RGL gave us, or failing that, the layout.
 *  h will be determined by the panel
 */
function calculateNewPanelCoordinates(rglLayoutItem, layout, rowHeight) {
    const coordinates = {};
    if (rglLayoutItem) {
        log.debug('Dropped layoutItem:');
        log.debugTable(rglLayoutItem);
        coordinates.x = rglLayoutItem.x;
        coordinates.y = rowsToPixels(rglLayoutItem.y, rowHeight);
        coordinates.w = rglLayoutItem.w;
    } else {
        // NJA: this is an unfortunate issue with Firefox & react-grid-layout.  Place the new panel at the bottom to avoid collisions.
        log.debug(
            'Bad layoutItem in Layout.jsx:calculateNewPanelCoordinates() - this is a known issue with Firefox & react-grid-layout. :('
        );
        coordinates.x = 0;
        const yRows = max(layout.map((item) => item.h + item.y)) || 0;
        coordinates.y = rowsToPixels(yRows, rowHeight);
        coordinates.w = DEFAULT_DROPPING_ITEM.w;
        log.debug(`y = ${coordinates.y}`);
    }
    return coordinates;
}

/*
 * Update the old layout with a new panel, replacing the "dropping element" RGL may have created during drag-and-drop.
 */
function updateLayoutWithNewPanel(panel, oldLayout, layoutOptions, rowHeight) {
    const { disableVerticalResize = false } = layoutOptions;
    const rglPlaceholderLayoutItem = oldLayout.find((layoutItem) => layoutItem.i === DEFAULT_DROPPING_ITEM.i) || {
        x: layoutOptions.x,
        y: pixelsToRows(layoutOptions.y),
        w: FULL_WIDTH,
        h: pixelsToRows(layoutOptions.h || layoutOptions.minH || DEFAULT_MIN_PANEL_HEIGHT, rowHeight),
    };

    const calculatedMinH = pixelsToRows(layoutOptions.minH || DEFAULT_MIN_PANEL_HEIGHT, rowHeight);
    const newLayoutItem = {
        ...rglPlaceholderLayoutItem,
        i: panel.props.id,
        minW: layoutOptions.minW || DEFAULT_MIN_PANEL_WIDTH,
        maxW: FULL_WIDTH,
        h: Math.max(rglPlaceholderLayoutItem.h, calculatedMinH),
        minH: calculatedMinH,
        resizeHandles: disableVerticalResize ? ['e'] : ['s', 'se', 'e'],
    };

    // replace the placeholder (if present) with the filled-in newLayoutItem
    return [...oldLayout.filter((layoutItem) => layoutItem.i !== DEFAULT_DROPPING_ITEM.i), newLayoutItem];
}

export default function Layout({
    children,
    containerSize,
    optimizeLayoutUpdates,
    loading,
    editing,
    dragging,
    rowHeight,
    droppingItem,
    panelProvider,
    onLayoutChange,
    onDraggingChange,
}) {
    const theme = useTheme();
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const pageId = usePageId();
    const [layout, layoutActions] = useLayoutReducer(rowHeight);
    const { panels, activeLayout, mainLayout } = layout;
    const hasHeight = containerSize?.height > 0;
    const hasPanels = panels.length > 0;
    const showDropMessage = !dragging && editing && hasHeight && !hasPanels;
    const [maximizedPanel, pageStateActions] = usePageStateValue({
        key: MAXIMIZED_PANEL_KEY,
    });
    const maximizedPanelId = useMemo(() => addPageIdPrefix(maximizedPanel, pageId), [maximizedPanel, pageId]);
    const layoutProps = useMemo(() => {
        return {
            compactType: 'vertical',
            cols: 12,
            containerPadding: [theme.spacing(outerSpacing / 2), theme.spacing(outerSpacing / 2)],
            rowHeight,
            margin: [0, 0],
            // This makes only the panel title a drag handle.  Attempting to drag a panel from
            // any other area will not start a drag operation.
            draggableHandle: '.panel-drag-handle',
            isDraggable: editing,
            isDroppable: editing,
            isResizable: editing,
            onLayoutChange: layoutActions.updateLayout,
            droppingItem,
        };
    }, [theme, editing, rowHeight, droppingItem, layoutActions]);

    const isVerticalResizeEnabled = useCallback(
        (panelId) => {
            const { disableVerticalResize = false } = layout.panelLayoutOptions[panelId];
            return !disableVerticalResize;
        },
        [layout]
    );

    const isAutoHeightPanel = useCallback(
        (panelId) => {
            const { autoHeight, flexHeight } = layout.panelLayoutOptions[panelId];
            // The flexHeight property overrides the autoHeight property.
            const auto = autoHeight && !flexHeight;
            // Force autoHeight when printing flexHeight or maximized panels.
            const force = isPrinting && (flexHeight || panelId === maximizedPanelId);
            return auto || force;
        },
        [layout, maximizedPanelId, isPrinting]
    );

    const onPanelAutoHeight = useCallback(
        (panelId, height) => {
            layoutActions.setPanelHeight(panelId, height);
        },
        [layoutActions]
    );

    const isMaximizedPanel = useCallback(
        (panelId) => {
            return panelId === maximizedPanelId;
        },
        [maximizedPanelId]
    );

    const onPanelMaximized = useCallback(
        (panelId, expanded) => {
            if (expanded) {
                pageStateActions.setValue(MAXIMIZED_PANEL_KEY, stripPageIdPrefix(panelId, pageId));
            } else {
                pageStateActions.clearValue(MAXIMIZED_PANEL_KEY);
            }
        },
        [pageStateActions, pageId]
    );

    const onRemovePanel = useCallback(
        (panelId) => {
            layoutActions.removePanel(panelId);
        },
        [layoutActions]
    );

    const layoutContextValue = useMemo(
        () => ({
            isAutoHeightPanel,
            onPanelAutoHeight,
            isMaximizedPanel,
            onPanelMaximized,
            onRemovePanel,
        }),
        [isAutoHeightPanel, onPanelAutoHeight, isMaximizedPanel, onPanelMaximized, onRemovePanel]
    );

    const getPanelLayoutOptions = useRef();
    getPanelLayoutOptions.current = (panel) => getLayoutOptions(panel, theme, isPrinting);

    const setPanels = useRef();
    setPanels.current = (newPanels) => {
        layoutActions.setPanels(newPanels, getPanelLayoutOptions.current, optimizeLayoutUpdates);
    };

    const resizeFlexPanels = useRef();
    resizeFlexPanels.current = (newContainerSize) => {
        if (!isPrinting) layoutActions.resizeFlexPanels(newContainerSize, theme.spacing(1));
    };

    const maximizeOrRestorePanel = useRef();
    maximizeOrRestorePanel.current = (panelId) => {
        if (panelId) {
            layoutActions.maximizePanel(panelId, containerSize, theme.spacing(outerSpacing));
        } else {
            layoutActions.restoreMaximizedPanel();
        }
    };

    const resizeMaximizedPanel = useRef();
    resizeMaximizedPanel.current = (newContainerSize) => {
        if (!isPrinting) {
            layoutActions.resizeMaximizedPanel(newContainerSize, theme.spacing(outerSpacing));
        }
    };

    const handlePanelResize = useRef();
    handlePanelResize.current = (newLayout, prevLayoutItem, resizedLayoutItem) => {
        log.debug('Panel resized');
        log.debugTable(prevLayoutItem);
        log.debugTable(resizedLayoutItem);

        // If the panel's height was manually changed, and vertical resizing is enabled for the panel,
        // disable auto-height for the panel.
        if (isVerticalResizeEnabled(resizedLayoutItem.i) && prevLayoutItem.h !== resizedLayoutItem.h) {
            layoutActions.setPanelAutoHeight(resizedLayoutItem.i, false);
        }
    };

    const handleDrop = useRef();
    handleDrop.current = (dropLayout, layoutItem, event) => {
        // The dragging state doesn't always get cleared before the drop handler is called.
        // Clear it now so it doesn't prevent the layout change handler from applying the
        // new, post-drop layout.
        onDraggingChange(false);

        const data = event.dataTransfer.getData('text/plain');
        const droppedItem = JSON.parse(data);

        log.debug(`Dropped ${droppedItem?.title} panel`);
        log.debugTable(dropLayout);

        const panelCoordinates = calculateNewPanelCoordinates(layoutItem, dropLayout, rowHeight);
        const newPanel = panelProvider(droppedItem.typeName, {
            ...panelCoordinates,
        });
        const layoutOptions = getPanelLayoutOptions.current(newPanel);
        const newLayout = updateLayoutWithNewPanel(newPanel, dropLayout, layoutOptions, rowHeight);

        log.debug(`Dropped ${droppedItem?.title} panel`);
        log.debugTable(layoutOptions);
        log.debugTable(newLayout);

        layoutActions.addPanel(newPanel, newLayout, layoutOptions);
    };

    const handleLayoutChange = useRef();
    handleLayoutChange.current = (newLayout) => {
        // Ignore layout changes while loading, dragging, or if the page is not a dashboard.
        if (loading || dragging) return;

        onLayoutChange(
            newLayout.map((layoutItem) => {
                const panel = find(panels, (p) => p.props.id === layoutItem.i);
                const height = isAutoHeightPanel(layoutItem.i) ? {} : { h: rowsToPixels(layoutItem.h, rowHeight) };
                return {
                    panel,
                    layout: {
                        x: layoutItem.x,
                        y: rowsToPixels(layoutItem.y, rowHeight),
                        w: layoutItem.w,
                        ...height,
                    },
                };
            })
        );
    };

    useEffect(() => {
        setPanels.current(Children.toArray(children));
    }, [children]);

    useEffect(() => {
        resizeFlexPanels.current(containerSize);
    }, [mainLayout, containerSize]);

    useEffect(() => {
        resizeMaximizedPanel.current(containerSize);
    }, [containerSize]);

    useEffect(() => {
        maximizeOrRestorePanel.current(maximizedPanelId);
    }, [maximizedPanelId]);

    useEffect(() => {
        handleLayoutChange.current(mainLayout);
    }, [mainLayout]);

    if (!containerSize) return null;

    return (
        <LayoutContext.Provider value={layoutContextValue}>
            <Fade in={showDropMessage}>
                <div
                    className={classes.dropMessage}
                    style={{
                        top: containerSize.height / 2 + HEADER_OFFSET,
                        width: containerSize.width,
                    }}
                >
                    <AddCircle />
                    <Trans>Drag panels here to add them to your dashboard</Trans>
                </div>
            </Fade>
            <ReactGridLayout
                className={classes.layout}
                width={containerSize.width}
                layout={layout[activeLayout]}
                {...layoutProps}
                onDragStart={() => onDraggingChange(true)}
                onDragStop={() => onDraggingChange(false)}
                onResizeStop={handlePanelResize.current}
                onDrop={handleDrop.current}
            >
                {panels.map((panel) => {
                    const { id } = panel.props;
                    const isMaximized = maximizedPanelId === id;
                    const isHidden = !!maximizedPanelId && !isMaximized;
                    return (
                        <div
                            key={id}
                            className={clsx({
                                [classes.layoutItemWrapper]: true,
                                [classes.hiddenLayoutItem]: isHidden,
                            })}
                        >
                            {panel}
                        </div>
                    );
                })}
            </ReactGridLayout>
        </LayoutContext.Provider>
    );
}

Layout.propTypes = {
    children: node.isRequired,
    containerSize: shape({
        width: number,
        height: number,
    }).isRequired,
    optimizeLayoutUpdates: bool,
    loading: bool,
    editing: bool,
    droppingItem: shape({
        i: string,
        w: number,
        h: number,
    }),
    rowHeight: number,
    panelProvider: func,
    onLayoutChange: func,
    onDraggingChange: func,
};

Layout.defaultProps = {
    optimizeLayoutUpdates: true,
    loading: false,
    editing: false,
    droppingItem: DEFAULT_DROPPING_ITEM,
    rowHeight: ROW_HEIGHT,
    panelProvider: noop,
    onLayoutChange: noop,
    onDraggingChange: noop,
};
